import { Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import AdminLayout from './components/AdminLayout';
import UserManagement from './components/UserManagement';
import AirlinerManagement from './components/AirlinerManagement';
import JetSetterManagement from './components/JetSetterManagement';
import SAXUserManagement from './components/SAXUserManagement';
import ContractManagement from './components/ContractManagement';
import CMSManagement from './components/CMSManagement';
import BlogManagement from './components/BlogManagement';
import BlogEditor from './components/BlogEditor';
import AdminBlogDetail from './components/AdminBlogDetail';
import AccountSettings from './components/AccountSettings';
import UserDetails from './components/UserDetails/UserDetails';
import UserEditor from './components/UserEditor/UserEditor';
import AircraftManagement from './components/AircraftManagement';
import FlightManagement from './components/FlightManagement';
import FlightFormPage from '../../airliner/pages/FlightForm/FlightFormPage';
import FlightDetailsPage from '../../airliner/pages/FlightDetails/FlightDetailsPage';
import CMSEditor from './components/CMSEditor';
import FlightSearchAnalytics from './components/DataAnalysis/FlightSearchAnalytics';
import FlightDetailsAnalytics from './components/DataAnalysis/FlightDetailsAnalytics';
import PagesViewAnalytics from './components/DataAnalysis/PagesViewAnalytics';
import UserAuthAnalytics from './components/DataAnalysis/UserAuthAnalytics';
import BookingProcessAnalytics from './components/DataAnalysis/BookingProcessAnalytics';
import SendContract from './components/SendContract';
import AddJetSetter from './components/AddJetSetter';
import AddAirliner from './components/AddAirliner';
import AddSubAdmin from './components/AddSubAdmin';
import SubAdminDetails from './components/SubAdminDetails';
import AddAircraft from '../../airliner/pages/AddAircraft/AddAircraft';
import AircraftDetails from '../../airliner/components/AircraftDetails/AircraftDetails';
import Notification from './components/Notification';
import AdminFlightManagement from './components/AdminFlightManagement';
import FlightChargesManagement from './components/FlightChargesManagement';
import SubAdminPermissionGuard from '../../components/ProtectedRoute/SubAdminPermissionGuard';

/**
 * Maps each module key to its admin route path.
 * Used to find the first accessible route for a SubAdmin after login.
 */
const MODULE_ROUTES = [
  { key: 'userManagement', path: '/admin/users' },
  { key: 'airliners', path: '/admin/airliners' },
  { key: 'jetsetters', path: '/admin/jetsetters' },
  { key: 'saxUsers', path: '/admin/sax-users' },
  { key: 'contracts', path: '/admin/contracts' },
  { key: 'cms', path: '/admin/cms' },
  { key: 'flights', path: '/admin/admin-flights' },
  { key: 'dataAnalysis', path: '/admin/data-analysis/flight-search' },
  { key: 'blogs', path: '/admin/blogs' },
  { key: 'messages', path: '/admin/notification' },
];

/**
 * Resolves the landing page for the current user:
 * - Full admins (user_type === 'Admin' or is_admin === 1) always land on /admin/users
 * - SubAdmins: wait for permissions to load, then land on the first accessible module.
 *   Falls back to /admin/settings if no module is accessible.
 *   Matches PHP's CheckPermission behaviour: user.index (users page) redirects to
 *   profile when the SubAdmin lacks that permission.
 */
const AdminDefaultRedirect = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { myPermissions } = useAppSelector((state) => state.admin);
  const { perms } = useAdminPermissions();

  const isAdmin = !user || user.user_type === 'Admin' || user.is_admin === 1;

  // Full admins go straight to users — no permission check needed
  if (isAdmin) {
    return <Navigate to="/admin/users" replace />;
  }

  // SubAdmin: wait for permissions to load before deciding where to send them.
  // Rendering null avoids firing any API calls for a route they may not have access to.
  if (!myPermissions) {
    return null;
  }

  // Find the first module this SubAdmin can access
  const firstAccessible = MODULE_ROUTES.find(({ key }) => perms[key]?.index === true);
  return <Navigate to={firstAccessible ? firstAccessible.path : '/admin/settings'} replace />;
};

const AdminDashboard = () => {
  return (
    <AdminLayout>
      <Routes>
        <Route path="/" element={<AdminDefaultRedirect />} />

        {/* ── User Management ── */}
        <Route path="/users" element={
          <SubAdminPermissionGuard moduleKey="userManagement">
            <UserManagement />
          </SubAdminPermissionGuard>
        } />
        <Route path="/users/:id" element={<UserDetails />} />
        <Route path="/users/:id/edit" element={<UserEditor />} />

        {/* ── Airliners ── */}
        <Route path="/airliners" element={
          <SubAdminPermissionGuard moduleKey="airliners">
            <AirlinerManagement />
          </SubAdminPermissionGuard>
        } />
        <Route path="/airliners/create" element={<AddAirliner />} />
        <Route path="/airliners/:id" element={<UserDetails />} />
        <Route path="/airliners/:id/aircrafts" element={<AircraftManagement />} />
        <Route path="/airliners/:airlinerId/aircrafts/create" element={<AddAircraft />} />
        <Route path="/airliners/:airlinerId/aircrafts/:id" element={<AircraftDetails />} />
        <Route path="/airliners/:airlinerId/aircrafts/:id/edit" element={<AddAircraft />} />
        <Route path="airliners/:airlinerId/flights" element={<FlightManagement />} />
        <Route path="airliners/:airlinerId/flights/:id" element={<FlightDetailsPage />} />
        <Route path="airliners/:airlinerId/flights/:flightId/edit" element={<FlightFormPage />} />
        <Route path="/aircraft/route/:aircraftId" element={<FlightFormPage />} />

        {/* ── JetSetters ── */}
        <Route path="/jetsetters" element={
          <SubAdminPermissionGuard moduleKey="jetsetters">
            <JetSetterManagement />
          </SubAdminPermissionGuard>
        } />
        <Route path="/jetsetters/create" element={<AddJetSetter />} />
        <Route path="/jetsetters/:id" element={<UserDetails />} />

        {/* ── SubAdmin (SAX) ── */}
        <Route path="/sax-users" element={
          <SubAdminPermissionGuard moduleKey="saxUsers">
            <SAXUserManagement />
          </SubAdminPermissionGuard>
        } />
        <Route path="/sax-users/create" element={<AddSubAdmin />} />
        <Route path="/sax-users/:id" element={<SubAdminDetails />} />

        {/* ── Contracts ── */}
        <Route path="/contracts" element={
          <SubAdminPermissionGuard moduleKey="contracts">
            <ContractManagement />
          </SubAdminPermissionGuard>
        } />
        <Route path="/contract/create" element={<SendContract />} />
        <Route path="/contract/:id/edit" element={<SendContract />} />

        {/* ── CMS ── */}
        <Route path="/cms" element={
          <SubAdminPermissionGuard moduleKey="cms">
            <CMSManagement />
          </SubAdminPermissionGuard>
        } />
        <Route path="/cms/create" element={<CMSEditor />} />
        <Route path="/cms/:id/edit" element={<CMSEditor />} />

        {/* ── Flight Management (admin view) ── */}
        <Route path="admin-flights" element={
          <SubAdminPermissionGuard moduleKey="flights">
            <AdminFlightManagement />
          </SubAdminPermissionGuard>
        } />
        <Route path="admin-flights/:id" element={<FlightDetailsPage />} />
        <Route path="admin-flights/:flightId/edit" element={<FlightFormPage />} />
        <Route path="/flight-charges" element={<FlightChargesManagement />} />

        {/* ── Data Analysis ── */}
        <Route path="/data-analysis/flight-search" element={
          <SubAdminPermissionGuard moduleKey="dataAnalysis">
            <FlightSearchAnalytics />
          </SubAdminPermissionGuard>
        } />
        <Route path="/data-analysis/flight-details" element={<FlightDetailsAnalytics />} />
        <Route path="/data-analysis/pages-view" element={<PagesViewAnalytics />} />
        <Route path="/data-analysis/user-auth" element={<UserAuthAnalytics />} />
        <Route path="/data-analysis/booking-process" element={<BookingProcessAnalytics />} />

        {/* ── Blogs ── */}
        <Route path="/blogs" element={
          <SubAdminPermissionGuard moduleKey="blogs">
            <BlogManagement />
          </SubAdminPermissionGuard>
        } />
        <Route path="/blogs/add" element={<BlogEditor />} />
        <Route path="/blogs/:id" element={<AdminBlogDetail />} />
        <Route path="/blogs/:id/edit" element={<BlogEditor />} />

        {/* ── Account Settings ── */}
        {/* No SubAdminPermissionGuard — account settings is always accessible to any Admin/SubAdmin */}
        <Route path="/settings" element={<AccountSettings />} />

        {/* ── Messages / Notifications ── */}
        <Route path="/notification" element={
          <SubAdminPermissionGuard moduleKey="messages">
            <Notification />
          </SubAdminPermissionGuard>
        } />
      </Routes>
    </AdminLayout>
  );
};

export default AdminDashboard;
